import config from "../../utils/config";
import { Card, CardContent, Typography, Pagination, Box, Grid, Container, Chip, Tabs, Tab } from "@mui/material";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "react-toastify";
import { useEffect } from "react";
import axios from "axios";

const numRows = 10;

function EventsIframe() {
  const { organizationId } = useParams();

  const [sharedEvents, setSharedEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [activeTab, setActiveTab] = useState("section");

  useEffect(() => {
    setPage(0);
    getEvents(0);
    getSharedEvents();
    getTotalEvents();
  }, [organizationId]); //eslint-disable-line

  const getSharedEvents = () => {
    axios
      .get(`${config.API_ENDPOINT}/events/shared/${organizationId}/all?active=true`)
      .then((res) => {
        setSharedEvents(res.data);
      })
      .catch(() => {
        toast.error("An unexpected error has occurred");
      });
  };

  const getEvents = (page) => {
    axios
      .get(
        `${config.API_ENDPOINT}/events/${organizationId}/all?active=true&shared=false&page=${page}&numRows=${numRows}`
      )
      .then((res) => {
        setEvents(res.data);
      })
      .catch(() => {
        toast.error("An unexpected error has occurred");
      });
  };

  const getTotalEvents = () => {
    axios
      .get(`${config.API_ENDPOINT}/events/${organizationId}/count?active=true&shared=false`)
      .then((res) => {
        setTotalRows(res.data.count);
      })
      .catch(() => {
        toast.error("An unexpected error has occurred");
      });
  };

  const EventCard = ({ event, isShared }) => {
    const eventData = isShared ? event.parentEvent : event;
    const linkTo = isShared
      ? `/iframes/event-details/shared/${event.organization}/${event._id}`
      : `/iframes/event-details/${event.organization}/${event._id}`;

    let startDate = moment(eventData.datetime).locale("en").format("dddd, DD/MM/YYYY HH:mm");
    let endDate = eventData.datetimeEnd
      ? moment(eventData.datetimeEnd).locale("en").format("dddd, DD/MM/YYYY HH:mm")
      : null;

    return (
      <Link to={linkTo} style={{ textDecoration: "none", color: "inherit" }}>
        <Card
          sx={{
            mb: 2,
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              elevation: 8,
              transform: "translateY(-4px)",
              boxShadow: "0 12px 20px rgba(0, 174, 239, 0.15)",
            },
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Image Section */}
              <Grid item xs={12} sm="auto">
                {eventData.imageURL ? (
                  <Box
                    component="img"
                    src={`${config.IMG_ENDPOINT}/uploads/${eventData.imageURL}`}
                    alt={eventData.name}
                    sx={{
                      height: 120,
                      width: 120,
                      objectFit: "contain",
                      borderRadius: 2,
                      backgroundColor: "#f5f5f5",
                      p: 1,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 120,
                      width: 120,
                      borderRadius: 2,
                      backgroundColor: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>
                      No image
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Content Section */}
              <Grid item xs={12} sm>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#00aeef", mb: 0.5 }}>
                  {eventData.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1, fontStyle: "italic" }}>
                  📍 {eventData.location}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.primary", mb: 1, lineHeight: 1.5 }}>
                  {eventData.briefDescription}
                </Typography>
              </Grid>

              {/* Date/Info Section */}
              <Grid item xs={12} sm="auto" sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>
                  {startDate}
                </Typography>
                {endDate && (
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1 }}>
                    to {endDate}
                  </Typography>
                )}
                {eventData.esnCardRequired && (
                  <Chip
                    label="ESNcard only"
                    size="small"
                    sx={{
                      mt: 1,
                      backgroundColor: "#3b2f74",
                      color: "white",
                      fontWeight: 600,
                      height: 24,
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Link>
    );
  };

  const sharedEventsUI = () => {
    const sortedSharedEvents = [...sharedEvents].sort(
      (a, b) => (b?.parentEvent?.datetime || 0) - (a?.parentEvent?.datetime || 0)
    );

    if (sortedSharedEvents.length === 0) return null;

    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#3b2f74" }}>
          🌍 Global Events
        </Typography>
        {sortedSharedEvents.map((event) => (
          <EventCard key={event._id} event={event} isShared={true} />
        ))}
      </Box>
    );
  };

  const eventsUI = () => {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#3b2f74" }}>
          🎪 Events from this section
        </Typography>
        {events.length > 0 ? (
          <>
            {events.map((event) => (
              <EventCard key={event._id} event={event} isShared={false} />
            ))}
            {Math.ceil(totalRows / numRows) > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={Math.ceil(totalRows / numRows)}
                  page={page + 1}
                  onChange={(_, newPage) => {
                    newPage--;
                    setPage(newPage);
                    getEvents(newPage);
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            No events available at the moment
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Tabs
        value={activeTab}
        onChange={(_, tab) => setActiveTab(tab)}
        sx={{ mb: 3 }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab value="section" label={`Section Events (${totalRows})`} />
        <Tab value="global" label={`Global Events (${sharedEvents.length})`} />
      </Tabs>

      {activeTab === "section" ? eventsUI() : sharedEventsUI()}
    </Container>
  );
}

export default EventsIframe;
